import type { FC } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { useSession, useCustomerInformation } from '@packages/utilities';

import type { GTMEventPageMetaData } from '../../hooks/useTracking';
import { useTracking } from '../../hooks/useTracking';
import { useIsBot } from '../../hooks/useIsBot';
import { GlycerinLoginStatus } from '../../types';

export type PageMetaDataProps = Omit<
  GTMEventPageMetaData,
  'event' | 'eventAction' | 'eventCategory' | 'eventLabel' | 'eventValue' | 'page'
> & {
  /** page meta data information */
  page: {
    /** the page type (e.g. 'serp', 'detailview') */
    type: string;
    /** optional template information */
    template?: string;
  };
  /** optional url pathname for useEffect deps array */
  pathname?: string;
};

/**
 * This component dispatches the following GTM events:
 * - dataLayerReady
 * - pageMetaData
 * */
export const PageMetaData: FC<PageMetaDataProps> = ({
  page,
  categoryPath,
  pathname,
  query,
  searchId,
}) => {
  const dispatchGTMEvent = useTracking();
  const isBot = useIsBot();
  const { isLoading, customerInformation } = useCustomerInformation();
  const { loggedIn } = useSession();
  const intl = useIntl();
  const [loginStatus, setLoginStatus] = useState<GlycerinLoginStatus>();

  const { type, template } = page;

  const getLoginStatus = useCallback(async () => {
    const { apiKeyManager } = await import('@empiriecom/mybuy-session/ApiKey');
    const apiKey = await apiKeyManager.getApiKey();
    const loginType = apiKeyManager.getTokenContents(apiKey)?.type;

    if (loginType && loginType === 1) {
      setLoginStatus(GlycerinLoginStatus.SOFTLOGGEDIN);
      return;
    }
    if (loginType && loginType >= 1) {
      setLoginStatus(GlycerinLoginStatus.LOGGEDIN);
      return;
    }
    setLoginStatus(GlycerinLoginStatus.NOTLOGGEDIN);
  }, []);

  useEffect(() => {
    getLoginStatus().catch(() => {
      // ignore
    });
  }, [getLoginStatus, loggedIn]);

  useEffect(() => {
    if (!isLoading && loginStatus && customerInformation) {
      dispatchGTMEvent({
        event: 'dataLayerReady',
        Sprache: intl.locale,
        LoginStatus: loginStatus,
        ...customerInformation,
        visitor: {
          ...customerInformation?.visitor,
          login: loggedIn,
        },
      });
    }
    // Fix for CORE-556: searchId has been added to trigger dataLayerReady on MBA/SERP changes.
    // TODO: rework events within tracking project
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, customerInformation?.visitor.login, intl.locale, loggedIn, searchId, loginStatus]);

  useEffect(() => {
    const event: GTMEventPageMetaData = {
      event: 'pageMetaData',
      page: {
        type,
        template,
        bot: isBot,
      },
      categoryPath,
      query,
      searchId,
      pageViewLanguage: intl.locale.split('-')[0],
    };
    if (typeof window !== 'undefined') {
      (window as any).pagetype = type;
    }
    dispatchGTMEvent(event);
    // remove categoryPath from array, it is not a primitive value and is kinda unpredictable
    // remove searchId from array, it changes for every settings change and is only relevant for MBA and SERP
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, pathname, template, query, isBot]);

  return <div data-testid="page-meta-data" />;
};
